import { RiskType } from "@/api/mutation/useAddRIskMutation";
import Table from "@/components/shared/Table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getSeverityAndColor } from "@/helperFuntions/riskSeverity";
import { useUser } from "@/hooks/useUser";

import {
  ImpactEnumSchema,
  ProbabilityEnumSchema,
  StatusEnumSchema,
} from "@backend/src/schemas/enums";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import NoRisk from "./NoRisk";
import useCreateDemoRiskQuary from "@/api/query/useCreateDemoRiskQuary";
import useAllRiskQuery from "@/api/query/useAllRisk";

// Define the type for the matrix object
type MatrixType = {
  [impact: string]: {
    [probability: string]: string[]; // Array of risk IDs
  };
};

const RiskMatrixTable = ({
  risks,
  onClick,
  projectId,
}: {
  risks: RiskType[];
  onClick: (data: RiskType) => void;
  projectId: string;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useUser();
  const impactLevels = Object.keys(ImpactEnumSchema).reverse();
  const probabilityLevels = Object.keys(ProbabilityEnumSchema);
  const allDemoRIskQuery = useCreateDemoRiskQuary(user?.userOrganisation[0].organisationId ?? "",projectId);
  const allRIskQuery = useAllRiskQuery(projectId ?? "");

  const matrix: MatrixType = {};
  risks
    .filter((r) => r.status == StatusEnumSchema.ACTIVE)
    .forEach((risk) => {
      const { impact, probability, riskNameId } = risk;
      if (!matrix[impact]) {
        matrix[impact] = {};
      }
      if (!matrix[impact][probability]) {
        matrix[impact][probability] = [];
      }
      matrix[impact][probability].push(riskNameId);
    });

  const handleClick = (riskNameId: string) => {
    const rindRisk = risks.find((r) => r.riskNameId == riskNameId);
    if (rindRisk) {
      onClick(rindRisk);
    }
  };
  function getOppositeIndex(index: number) {
    const oppositeIndexMap = [3, 2, 1, 0]; // Define opposite indices
    return index >= 0 ? oppositeIndexMap[index] : -1; // Return -1 if the impact is not found
  }

  return (
    <div className="flex gap-10 justify-between h-[35rem] w-full">
      <table className="">
        {/* Table Header */}
        <thead>
          <tr>
            <th className="bg-white"></th>
            {probabilityLevels.map((level) => (
              <th key={level} className="bg-teal-500 text-white p-2">
                {t(level)}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {impactLevels.map((impact) => (
            <tr key={impact} className="h-9">
              <td
                className={` bg-teal-500 text-white !p-5 font-semibold   relative`}
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-90 ">
                  {t(impact)}
                </div>
              </td>

              {probabilityLevels.map((probability) => {
                const risksInCell =
                  matrix[impact] && matrix[impact][probability]
                    ? matrix[impact][probability]
                    : null;
                const truncateLength = risksInCell
                  ?.join(",")
                  .slice(0, 35)
                  .split(",").length;
                const cellColor = getSeverityAndColor(
                  getOppositeIndex(impactLevels.findIndex((t) => t == impact)),
                  probabilityLevels.findIndex((t) => t == probability)  ,
                  user?.userOrganisation[0].organisation?.severityTypes??[]
                ).color;
                const truncatedRisks = risksInCell?.slice(0, truncateLength);

                return (
                  <td
                    key={probability}
                    className={`p-2 border text-center  max-h-32 max-w-32 h-32 w-32 `}
                    style={{ backgroundColor: cellColor }}
                  >
                    {truncatedRisks?.map((e, i) => {
                      const fullText =
                        risks?.find((r) => r.riskNameId === e)?.riskNameId || e;
                      const truncatedText =
                        fullText.length > 35
                          ? `${fullText.slice(0, 35)}...`
                          : fullText;

                      return (
                        <TooltipProvider key={e} delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                className="hover:underline underline-offset-1 cursor-pointer transition-all duration-100 truc"
                                onClick={() => handleClick(e)}
                              >
                                {truncatedText}
                                {i !== truncatedRisks.length - 1 ? ", " : ""}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-64 text-wrap text-sm">
                                {risks?.find((r) => r.riskNameId === e)?.title}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                    {risksInCell && risksInCell?.length > (truncateLength ?? 0)
                      ? "..."
                      : ""}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="w-[30rem] mr-auto">
        <Table
          data={risks.filter((r) => r.status == StatusEnumSchema.ACTIVE) ?? []}
          className="!p-0"
          columnDef={[
            {
              header: t("riskNameId"),
              key: "riskNameId",
              className: () => {
                return "!w-24";
              },
              onCellRender: (data: RiskType) => {
                return (
                  <>
                    <div
                      className="text-primary-500 underline truncate w-24 cursor-pointer text-start"
                      onClick={() => {
                        navigate(`/risk/${risks[0].projectId}`, {
                          state: data,
                        });
                        window.history.replaceState({}, "");
                      }}
                    >
                      <TooltipProvider key={data.riskNameId} delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div>{data.riskNameId}</div>
                          </TooltipTrigger>
                          <TooltipContent>{data.riskNameId}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </>
                );
              },
              sorting: true,
            },
            {
              header: t("title"),
              key: "title",
              sorting: true,
              onCellRender: (data) => {
                return (
                  <div className="w-56">

                    <TooltipProvider key={data.riskNameId} delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className=" text-start truncate ">{t(data.title)}</div>
                        </TooltipTrigger>
                        <TooltipContent><p className="max-w-64 text-wrap">{data.title}</p></TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                );
              },
            },
          ]}
          noDataFound={() => {
            return (
              <NoRisk
                projectId={projectId ?? ""}
                refetch={() => {
                  allDemoRIskQuery.refetch().then(() => allRIskQuery.refetch());
                }}
                filtered={false}
                noShowImage={true}
              />
            );
          }}
        />
      </div>
    </div>
  );
};

export default RiskMatrixTable;
